/**
 * Product gallery lightbox: click an image to view it fullscreen.
 * Uses document-level delegation so it works regardless of when/where the gallery is rendered.
 */
(function () {
  'use strict';

  const DEBUG = true;
  function log(...args) {
    if (DEBUG) console.log('[dg-lightbox]', ...args);
  }

  function escapeHtml(str) {
    if (!str) return '';
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  function openLightbox(urls, index) {
    log('openLightbox called', { urls: urls?.length, index });
    const i = Math.max(0, Math.min(index, urls.length - 1));
    const overlay = document.createElement('div');
    overlay.className = 'dg-lightbox';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'View product image fullscreen');

    overlay.innerHTML = `
      <button type="button" class="dg-lightbox__close" aria-label="Close">×</button>
      <div class="dg-lightbox__content">
        <img src="${escapeHtml(urls[i])}" alt="Product image" class="dg-lightbox__img" loading="eager">
      </div>
      ${urls.length > 1 ? `
        <button type="button" class="dg-lightbox__arrow dg-lightbox__arrow--prev" aria-label="Previous image">‹</button>
        <button type="button" class="dg-lightbox__arrow dg-lightbox__arrow--next" aria-label="Next image">›</button>
        <span class="dg-lightbox__counter" aria-live="polite">${i + 1} / ${urls.length}</span>
      ` : ''}
    `;

    const style = document.createElement('style');
    style.textContent = `
      .dg-lightbox {
        position: fixed;
        inset: 0;
        z-index: 10000;
        background: rgba(0,0,0,0.95);
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: zoom-out;
      }
      .dg-lightbox__content {
        max-width: 100%;
        max-height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 2rem;
        cursor: default;
      }
      .dg-lightbox__img {
        max-width: 100%;
        max-height: 90vh;
        width: auto;
        height: auto;
        object-fit: contain;
      }
      .dg-lightbox__close {
        position: absolute;
        top: 1rem;
        right: 1rem;
        width: 2.5rem;
        height: 2.5rem;
        border: none;
        background: rgba(255,255,255,0.1);
        color: #fff;
        font-size: 1.75rem;
        line-height: 1;
        cursor: pointer;
        border-radius: 4px;
        z-index: 2;
      }
      .dg-lightbox__close:hover {
        background: rgba(255,255,255,0.2);
      }
      .dg-lightbox__arrow {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        width: 3rem;
        height: 3rem;
        border: none;
        background: rgba(255,255,255,0.1);
        color: #fff;
        font-size: 2rem;
        line-height: 1;
        cursor: pointer;
        border-radius: 4px;
        z-index: 2;
      }
      .dg-lightbox__arrow:hover {
        background: rgba(255,255,255,0.2);
      }
      .dg-lightbox__arrow--prev { left: 1rem; }
      .dg-lightbox__arrow--next { right: 1rem; }
      .dg-lightbox__counter {
        position: absolute;
        bottom: 1rem;
        left: 50%;
        transform: translateX(-50%);
        color: rgba(255,255,255,0.8);
        font-size: 0.9rem;
        z-index: 2;
      }
    `;
    document.head.appendChild(style);

    let currentIdx = i;

    function updateImage() {
      const img = overlay.querySelector('.dg-lightbox__img');
      const counter = overlay.querySelector('.dg-lightbox__counter');
      if (img) img.src = urls[currentIdx];
      if (counter) counter.textContent = `${currentIdx + 1} / ${urls.length}`;
    }

    function close() {
      document.body.style.overflow = '';
      overlay.remove();
      document.removeEventListener('keydown', onKeydown);
    }

    function onKeydown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
        return;
      }
      if (urls.length <= 1) return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        currentIdx = (currentIdx - 1 + urls.length) % urls.length;
        updateImage();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        currentIdx = (currentIdx + 1) % urls.length;
        updateImage();
      }
    }

    overlay.addEventListener('click', (e) => {
      if (e.target === overlay || e.target.classList.contains('dg-lightbox__close')) {
        e.preventDefault();
        close();
      }
    });

    overlay.querySelector('.dg-lightbox__content')?.addEventListener('click', (e) => e.stopPropagation());

    const prevBtn = overlay.querySelector('.dg-lightbox__arrow--prev');
    const nextBtn = overlay.querySelector('.dg-lightbox__arrow--next');
    if (prevBtn) {
      prevBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        currentIdx = (currentIdx - 1 + urls.length) % urls.length;
        updateImage();
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        currentIdx = (currentIdx + 1) % urls.length;
        updateImage();
      });
    }

    document.body.style.overflow = 'hidden';
    document.body.appendChild(overlay);
    document.addEventListener('keydown', onKeydown);
  }

  // Document-level delegation: capture phase so we run before anything else
  log('script loaded, attaching click listener (capture phase)');

  document.addEventListener(
    'click',
    function handleGalleryClick(e) {
      const target = e.target;
      const targetTag = target?.tagName;
      const targetClass = target?.className;

      // Log every click on product page (throttle: only log if near gallery)
      const inGallery = target?.closest?.('.dg-pdp__gallery');
      if (inGallery) {
        log('click in gallery', { targetTag, targetClass, target: target });
      }

      // Ignore clicks on carousel arrows
      if (target?.closest?.('[data-dg-prev], [data-dg-next]')) {
        log('skip: clicked arrow');
        return;
      }

      const scope = target?.closest?.('.dg-pdp__gallery');
      if (!scope) return;

      const slides = Array.from(scope.querySelectorAll('[data-dg-slide][data-dg-lightbox-url]'));
      const urls = slides.map((s) => s.getAttribute('data-dg-lightbox-url')).filter(Boolean);
      if (!urls.length) {
        if (inGallery) log('skip: no urls', { slidesCount: slides.length });
        return;
      }

      // Click might hit carousel/track container, not the slide - use carousel's current index
      let idx = -1;
      const slide = target?.closest?.('[data-dg-slide][data-dg-lightbox-url]');
      if (slide) {
        idx = slides.indexOf(slide);
      }
      if (idx < 0) {
        const carousel = scope.querySelector('[data-dg-carousel]');
        idx = Math.max(0, parseInt(carousel?.dataset?.index || '0', 10) || 0);
      }
      idx = Math.max(0, Math.min(idx, urls.length - 1));

      log('opening lightbox', { idx, urlsCount: urls.length });
      e.preventDefault();
      e.stopPropagation();

      openLightbox(urls, idx);
    },
    true
  );

  // Debug: log gallery state on load
  if (typeof document !== 'undefined') {
    const check = () => {
      const galleries = document.querySelectorAll('.dg-pdp__gallery');
      const slides = document.querySelectorAll('.dg-pdp__gallery [data-dg-slide][data-dg-lightbox-url]');
      log('DOM check', { galleries: galleries.length, slides: slides.length, slideUrls: Array.from(slides).map((s) => !!s.getAttribute('data-dg-lightbox-url')) });
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', check);
    } else {
      check();
    }
  }
})();
